import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  confirmPasswordReset,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { auth } from '../../config/firebase'; // Adjust to your file structure

const ResetPasswordForm: React.FC = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [oobCode, setOobCode] = useState('');
  const [codeValid, setCodeValid] = useState(false);
  const [env, setEnv] = useState<'development' | 'staging' | 'production'>('production');
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('oobCode');
    const envParam = params.get('env');

    if (envParam === 'development' || envParam === 'staging' || envParam === 'production') {
      setEnv(envParam);
    }

    if (!code) {
      setError('Missing reset code.');
      return;
    }

    setOobCode(code);

    verifyPasswordResetCode(auth, code)
      .then(() => setCodeValid(true))
      .catch(() => setError('Invalid or expired password reset link.'));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setMessage('Password reset successful! Redirecting to login...');

      // Redirect based on env after short delay
      setTimeout(() => {
        const redirectMap = {
          development: 'http://localhost:3000/login',
          staging: 'https://staging.yourdomain.com/login',
          production: 'https://yourdomain.com/login',
        };
        window.location.href = redirectMap[env];
      }, 2500);
    } catch (err) {
      console.error(err);
      setError('Failed to reset password. Please try again or request a new link.');
    }
  };

  if (!codeValid && !error) return <p>Validating reset link...</p>;
  if (error) return <p className="text-red-600 text-center mt-8">{error}</p>;

  return (
    <div className="min-h-screen w-full flex flex-col items-center px-4 sm:px-6 bg-white">
      <form onSubmit={handleSubmit} className="w-full max-w-md flex flex-col items-center space-y-6 p-6 bg-white login-form mt-8 sm:mt-32">
        <h2 className="text-2xl mb-6 text-center">Reset Your Password</h2>

        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="password"
            placeholder="New Password"
            className="w-full border-b border-gray-300 px-0 py-2 text-sm focus:outline-none placeholder:text-gray-500"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </div>

        <div className="w-full sm:w-[25.8125rem]">
          <input
            type="password"
            placeholder="Confirm Password"
            className="w-full border-b border-gray-300 px-0 py-2 text-sm focus:outline-none placeholder:text-gray-500"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        {error && <p className="text-red-600 text-sm">{error}</p>}
        {message && <p className="text-green-600 text-sm">{message}</p>}

        <button
          type="submit"
          className="w-full sm:w-[25.8125rem] border px-5 py-3 sm:py-4 rounded-full text-sm sm:text-base text-black font-medium hover:bg-gray-200 transition flex items-center justify-center gap-3 uppercase min-h-[3rem]"
          style={{ paddingTop: "1rem" }}
        >
          Reset Password
        </button>
      </form>
    </div>
  );
};

export default ResetPasswordForm;
